// src/lib/resources/data/glossary/source-urls.ts
//
// Map of known benchmark-source publisher names to canonical URLs.
//
// Source citations on `segmentBenchmarks` rows are authored as free-text
// in `data/glossary.yaml` (e.g. "WordStream 2024", "Triple Whale Pulse
// data"). This map lets the BenchmarksTable render the recognizable ones
// as `rel="nofollow noopener noreferrer"` links to the publisher's
// canonical landing page without changing the YAML schema.
//
// Match strategy in `resolveSourceUrl()` below:
//   1. Lowercase the source string.
//   2. Strip year tokens (2023–2026), quarter tokens (Q1–Q4),
//      and trailing parenthetical qualifiers.
//   3. Walk this map's keys longest-first; the first key that is a
//      prefix of the cleaned source wins. Longest-first matters because
//      "wordstream / localiq" should match the joined key, not the
//      shorter "wordstream".
//
// Sources that don't match (yet) render as plain text — no broken
// links shipped. To add a new publisher, add a lowercase key here
// pointing at its homepage or benchmark hub. Year-specific report
// URLs are deliberately NOT used because publishers move their report
// pages around and 404s on cited sources damage EEAT more than a stable
// homepage link does.
//
// Coverage target: the ~30 most-cited publishers in the YAML. The full
// citation set has ~150 distinct strings due to year/qualifier variants
// of the same publisher, all collapsed by the prefix match above.

export const SOURCE_URLS: Readonly<Record<string, string>> = {
  // Performance-marketing benchmark publishers (cited most frequently)
  'wordstream / localiq': 'https://www.wordstream.com/blog/ws/2022/05/19/online-advertising-benchmarks',
  'wordstream / smart insights': 'https://www.wordstream.com',
  'wordstream facebook ads benchmarks': 'https://www.wordstream.com/blog/ws/2022/05/19/online-advertising-benchmarks',
  'wordstream google ads benchmarks': 'https://www.wordstream.com/blog/ws/2022/05/19/online-advertising-benchmarks',
  'wordstream': 'https://www.wordstream.com',
  'localiq search advertising benchmarks': 'https://localiq.com/blog/search-advertising-benchmarks/',
  'localiq': 'https://localiq.com',
  'firstpagesage': 'https://firstpagesage.com',
  'triple whale facebook ads benchmarks': 'https://www.triplewhale.com/blog',
  'triple whale facebook benchmarks': 'https://www.triplewhale.com/blog',
  'triple whale benchmarks': 'https://www.triplewhale.com/blog',
  'triple whale pulse data': 'https://www.triplewhale.com/pulse',
  'triple whale industry report': 'https://www.triplewhale.com/blog',
  'triple whale / digital applied': 'https://www.triplewhale.com',
  'triple whale / lebesgue': 'https://www.triplewhale.com',
  'triple whale': 'https://www.triplewhale.com',
  'motion creative benchmarks': 'https://motionapp.com/resources',
  'motion thumbstop pulse': 'https://motionapp.com/resources',
  'motion metrics cheat sheet': 'https://motionapp.com/resources',
  'motion help center': 'https://help.motionapp.com',
  'motion / mhi growth engine': 'https://motionapp.com/resources',
  'motion / marpipe': 'https://motionapp.com/resources',
  'motion / triple whale': 'https://motionapp.com/resources',
  'motion': 'https://motionapp.com',
  'mhi growth engine': 'https://www.growthengine.ai',
  'common thread collective dtc index': 'https://commonthreadcollective.com',
  'common thread collective': 'https://commonthreadcollective.com',
  'northbeam ad fatigue guide': 'https://www.northbeam.io/resources',
  'northbeam supplement vertical study': 'https://www.northbeam.io/resources',
  'northbeam ecommerce benchmark': 'https://www.northbeam.io/resources',
  'northbeam': 'https://www.northbeam.io',
  'nielsen compass norms': 'https://www.nielsen.com',
  'nielsen 123-brand replication': 'https://www.nielsen.com',
  'nielsen': 'https://www.nielsen.com',
  'appsflyer / adjust': 'https://www.appsflyer.com',
  'appsflyer': 'https://www.appsflyer.com',
  'adjust': 'https://www.adjust.com',
  'klaviyo': 'https://www.klaviyo.com',
  'unbounce conversion benchmark': 'https://unbounce.com/conversion-benchmark-report',
  'unbounce': 'https://unbounce.com',
  'bessemer state of the cloud': 'https://www.bvp.com/cloud-100',
  'openview': 'https://openviewpartners.com',
  'optifai': 'https://optifai.com',
  'sprout social index': 'https://sproutsocial.com/insights/data',
  'sprout social social listening reports': 'https://sproutsocial.com/insights',
  'sprout social': 'https://sproutsocial.com/insights',
  'rival iq': 'https://www.rivaliq.com/blog/social-media-industry-benchmark-report/',
  'socialinsider': 'https://www.socialinsider.io/blog',
  'rival iq / socialinsider': 'https://www.rivaliq.com/blog',
  'shopify plus industry report': 'https://www.shopify.com/plus/research',
  'shopify plus report': 'https://www.shopify.com/plus/research',
  'shopify enterprise data': 'https://www.shopify.com/enterprise',
  'shopify / wordstream': 'https://www.shopify.com/blog',
  'shopify': 'https://www.shopify.com',
  'polar analytics dtc benchmark report': 'https://www.polaranalytics.com/blog',
  'polar analytics': 'https://www.polaranalytics.com/blog',
  'recharge state of subscription commerce': 'https://rechargepayments.com/resources',
  'recharge': 'https://rechargepayments.com',
  'recurly subscription benchmarks': 'https://recurly.com/research',
  'recurly': 'https://recurly.com',
  'revenuecat state of subscription apps': 'https://www.revenuecat.com/state-of-subscription-apps',
  'revenuecat': 'https://www.revenuecat.com',
  'sensor tower': 'https://sensortower.com/research',
  'data.ai': 'https://www.data.ai',
  'statista dtc report': 'https://www.statista.com',
  'statista': 'https://www.statista.com',
  'statcounter': 'https://gs.statcounter.com/search-engine-market-share',
  'siege media benchmarks': 'https://www.siegemedia.com/marketing/seo-benchmarks',
  'siege media': 'https://www.siegemedia.com',
  'webfx tiktok benchmarks': 'https://www.webfx.com/blog',
  'webfx': 'https://www.webfx.com',
  'owlclaw youtube benchmarks': 'https://owlclaw.com/blog',
  'owlclaw': 'https://owlclaw.com',
  'opusclip retention study': 'https://www.opus.pro',
  'tiktok ads help center': 'https://ads.tiktok.com/help',
  'tikadsuite / tiktok ads help': 'https://ads.tiktok.com/help',
  'ta monroe / cometly': 'https://tamonroe.com/blog',
  'ta monroe / firstpagesage': 'https://tamonroe.com/blog',
  'ta monroe': 'https://tamonroe.com/blog',
  'cometly': 'https://www.cometly.com',
  'mida checkout benchmarks': 'https://www.mida.so/blog',
  'mida': 'https://www.mida.so',
  'rivo benchmarks': 'https://www.rivo.io',
  'mobiloud / eightx dtc benchmarks': 'https://www.mobiloud.com/blog',
  'mobiloud': 'https://www.mobiloud.com',
  'mutesix': 'https://mutesix.com',
  'lebesgue': 'https://lebesgue.io',
  'jumpfly': 'https://www.jumpfly.com',
  'tinuiti': 'https://tinuiti.com',
  'tinuiti benchmark report': 'https://tinuiti.com/insights',
  'inbeat / swell dtc stats': 'https://inbeat.agency/blog',
  'inbeat': 'https://inbeat.agency',
  'yotpo dtc': 'https://www.yotpo.com',
  'yotpo': 'https://www.yotpo.com',
  'store growers / strike social': 'https://www.storegrowers.com/blog',
  'store growers': 'https://www.storegrowers.com/blog',
  'strike social': 'https://strikesocial.com/blog',
  'strike social / varos': 'https://strikesocial.com/blog',
  'stackmatix / databox frequency survey': 'https://databox.com/marketing-benchmarks',
  'databox': 'https://databox.com',
  'saras analytics': 'https://sarasanalytics.com',
  'rival iq social media benchmark report': 'https://www.rivaliq.com/blog/social-media-industry-benchmark-report/',
  'power digital': 'https://powerdigitalmarketing.com',
  'ppc chief': 'https://ppcchief.com',
  'quimby digital': 'https://www.quimbydigital.com',
  'semetis esov analysis': 'https://www.semetis.com',
  'semetis': 'https://www.semetis.com',
  'warc': 'https://www.warc.com',
  'tailored edge / triple whale tiktok benchmarks': 'https://tailorededge.com',
  'your marketing partners ftir analyses': 'https://yourmarketingpartners.com',
  'your marketing partners': 'https://yourmarketingpartners.com',
  'ecomhint': 'https://ecomhint.com',
  'innovid global benchmarks': 'https://www.innovid.com',
  'innovid': 'https://www.innovid.com',
  'meta business help center': 'https://www.facebook.com/business/help',
  'meta': 'https://www.facebook.com/business/help',
}

/**
 * Resolve a raw source citation string to its canonical publisher URL,
 * or null when the publisher isn't in the known-URL map. Pure function —
 * safe to call during SSR and at build time.
 *
 * Examples:
 *   resolveSourceUrl('WordStream 2024')                    → 'https://www.wordstream.com'
 *   resolveSourceUrl('Triple Whale Pulse data')            → 'https://www.triplewhale.com/pulse'
 *   resolveSourceUrl('Some publisher not in the map 2025') → null
 *   resolveSourceUrl('')                                    → null
 */
export function resolveSourceUrl(source: string): string | null {
  if (!source) return null

  // Normalize: lowercase + strip common qualifier tokens that vary per
  // citation but don't change the publisher identity. Year tokens (4-digit
  // 20xx) and quarter tokens (Qx) are stripped; remaining whitespace is
  // collapsed.
  const cleaned = source
    .toLowerCase()
    .replace(/\([^)]*\)/g, '') // drop parenthetical qualifiers like "(Facebook Benchmarks)"
    .replace(/\b20\d{2}\b/g, '') // drop year tokens
    .replace(/\bq[1-4]\b/gi, '') // drop quarter tokens
    .replace(/\s+/g, ' ')
    .trim()

  // Longest-prefix match. Longest first so "wordstream / localiq" beats
  // "wordstream" alone when both are present in the map.
  const keys = Object.keys(SOURCE_URLS).sort((a, b) => b.length - a.length)
  for (const key of keys) {
    if (cleaned.startsWith(key)) return SOURCE_URLS[key] ?? null
  }
  return null
}
